import React, { useEffect, useState } from "react";
import supabase from "../../../utils/supabase";
import { rosterGoogleClassroomClass } from "../../../utils/rosterGoogleClassroomClass";
import { toast } from "react-toastify";
import ConfirmDeleteClassModal from "../../modals/ConfirmDeleteClassModal";
import KidLoginCardsModal from "../../modals/KidLoginCardsModal";
import { useUiLangStore } from "../../../stores/uiLang";
import { Trash2, Settings, RefreshCcw, Printer } from "lucide-react";
import { useT } from "../../../stores/uiLang";
import { TD_T } from "../../../i18n/teacherDashboard";

interface ClassCardProps {
  cls: {
    id: string;
    name: string;
    description?: string;
    semester: string;
    settings: {
      class_time: string;
    };
    grade_type: "ABC" | "1234" | "%";
    student_count?: number;
    google_class_id?: string;
    last_synced?: string;
    join_code?: string | null;
  };
  onManage: () => void;
  onRosterComplete?: (results: any) => void;
  onDelete?: () => void;
}

export default function ClassCard({
  cls,
  onManage,
  onRosterComplete,
  onDelete,
}: ClassCardProps) {
  const t = useT(TD_T);
  const [studentsCount, setStudentsCount] = useState(0);
  const [isRostering, setIsRostering] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isCardsOpen, setIsCardsOpen] = useState(false);
  const isEs = useUiLangStore((s) => s.lang) === "es";
  async function loadStudentsCount() {
    const { count, error } = await supabase
      .from("class_students")
      .select("*", { count: "exact", head: true })
      .eq("class_id", cls.id);

    if (error) {
      console.error("Error getting students count", error);
      setStudentsCount(0);
      return;
    }

    setStudentsCount(count);
  }

  const handleRoster = async () => {
    setIsRostering(true);
    try {
      const result = await rosterGoogleClassroomClass(cls.id);

      if (result.error) {
        toast.error(result.error);
        return;
      }

      if (result) {
        const { importedCount, failedCount } = result;

        if (importedCount > 0) {
          toast.success(
            `Successfully rostered ${importedCount} new students to ${cls.name}`
          );
        } else {
          toast.info("No new students found to roster");
        }

        if (failedCount > 0) {
          toast.warning(
            `${failedCount} students failed to roster. Check the details below.`
          );
        }

        // Refresh student count
        await loadStudentsCount();

        // Notify parent component about roster results
        if (onRosterComplete) {
          onRosterComplete(result);
        }
      }
    } catch (error) {
      toast.error("Failed to roster class from Google Classroom");
      console.error(error);
    } finally {
      setIsRostering(false);
    }
  };

  useEffect(() => {
    loadStudentsCount();
  }, []);

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden h-full flex flex-col">
      <div className="p-6 flex-1 flex flex-col">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              {cls.name}
            </h3>
            <p className="text-sm text-gray-500 mb-2">{cls.semester}</p>
          </div>
          <div className="flex items-center space-x-2">
            {cls.google_class_id && (
              <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                Google Classroom
              </span>
            )}
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
              {t.activeClass}
            </span>
          </div>
        </div>

        {cls.description && (
          <p className="text-sm text-gray-600 mb-4">{cls.description}</p>
        )}

        <div className="grid grid-cols-2 gap-4 text-sm flex-1">
          <div>
            <p className="text-gray-500">{t.classTime}</p>
            <p className="text-gray-900">
              {cls.settings.class_time && cls.settings.class_time !== "Not specified"
                ? cls.settings.class_time
                : t.notSpecified}
            </p>
          </div>
          <div>
            <p className="text-gray-500">{t.students}</p>
            <p className="text-gray-900">{studentsCount || 0}</p>
          </div>
          <div>
            <p className="text-gray-500">{t.gradeType}</p>
            <p className="text-gray-900">{cls.grade_type}</p>
          </div>
          <div>
            <p className="text-gray-500">{t.joinCode}</p>
            {cls.join_code ? (
              <div className="flex items-center space-x-2">
                <p className="text-gray-900 font-mono text-sm">
                  {cls.join_code}
                </p>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(cls.join_code!);
                    toast.info("Join code copied to clipboard!");
                  }}
                  className="text-blue-600 hover:text-blue-800"
                  title={t.copyJoinCode}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                </button>
              </div>
            ) : (
              <p className="text-gray-400 text-sm">{t.noJoinCode}</p>
            )}
          </div>
          {cls.google_class_id && cls.last_synced && (
            <div className="col-span-2">
              <p className="text-gray-500">{t.lastSynced}</p>
              <p className="text-gray-900">
                {new Date(cls.last_synced).toLocaleDateString()} at{" "}
                {new Date(cls.last_synced).toLocaleTimeString()}
              </p>
            </div>
          )}
        </div>
        <div className="flex space-x-2 mt-5 justify-end">
          {cls.google_class_id && (
            <button
              onClick={handleRoster}
              disabled={isRostering}
              className="inline-flex items-center px-3 py-1.5 border border-blue-300 rounded-md text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isRostering ? (
                <>
                  <RefreshCcw className="w-4 h-4 mr-2" />
                  {t.rostering}
                </>
              ) : (
                <>
                  <RefreshCcw className="w-4 h-4 mr-2" />
                  {t.roster}
                </>
              )}
            </button>
          )}
          <button
            onClick={() => setIsCardsOpen(true)}
            className="inline-flex items-center px-3 py-1.5 border border-indigo-300 rounded-md text-sm font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100"
          >
            <Printer className="w-4 h-4 mr-2" />
            {isEs ? "Imprimir tarjetas" : "Print login cards"}
          </button>
          <button onClick={onManage} className="btn btn-secondary btn-small">
            <Settings className="w-4 h-4 mr-2" />
            {t.manage}
          </button>
          <button
            onClick={() => setIsDeleteModalOpen(true)}
            className="inline-flex items-center px-3 py-1.5 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            {t.delete}
          </button>
        </div>
      </div>

      <KidLoginCardsModal
        isOpen={isCardsOpen}
        onClose={() => setIsCardsOpen(false)}
        classId={cls.id}
        className={cls.name}
      />

      <ConfirmDeleteClassModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        cls={cls}
        onClassDeleted={() => {
          // Refresh the classes list in the parent component
          onDelete();
        }}
      />
    </div>
  );
}
